import { useCallback, useEffect } from 'react';
import {
  ActionOutboxEntityTypeForBulkEdit,
  bulkEditInProgress,
  hasPrimaryAffectedIds,
} from 'src/components/common/BulkActions/BulkEditStatus';
import {
  BulkEditStage,
  useBulkEditHubContext,
} from 'src/contexts/BulkEditHubContext';
import { Content } from 'src/contexts/ContentContext';
import { ConfirmDialog } from 'src/core/interim/dialogs/ConfirmDialog';
import { Button, Stack } from 'src/core/ui';
import { useBasicDialog } from 'src/hooks/useBasicDialog';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { ContentId } from 'src/utils/constants/contentId';
import {
  BulkEditPreviewWithDetails,
  BulkEditProgress,
  BulkEditStep,
  Feature,
} from 'src/WebApiController';

export const BulkEditFooter = ({
  entityType,
  primaryEntityTypesAffected,
  isLoading,
  hasBackgroundBulkEditFeature,
  onClose,
  onSubmit,
  onBeforeSubmit,
  disabled,
  submittButtonRef,
  onCloseContentIdOverride,
  onSubmitContentIdOverride,
}: {
  entityType: ActionOutboxEntityTypeForBulkEdit;
  primaryEntityTypesAffected?: ActionOutboxEntityTypeForBulkEdit[];
  isLoading?: boolean;
  hasBackgroundBulkEditFeature: boolean;
  onBeforeSubmit?: () => Promise<boolean> | boolean;
  onSubmit: (
    onPreviewCallback?: (preview: BulkEditPreviewWithDetails) => void
  ) => void;
  onClose: (
    newProgress?: BulkEditProgress,
    closeMainDialogForPopover?: boolean
  ) => void;
  disabled?: boolean;
  submittButtonRef: React.RefObject<HTMLButtonElement>;
  onCloseContentIdOverride?: ContentId;
  onSubmitContentIdOverride?: ContentId;
}) => {
  const {
    stage,
    progress,
    preview,
    setStage,
    setPreview,
    setProgress,
    setErrors,
    setWarnings,
    retryableEntities,
    progressBeforeRetry,
    onRetryCancel,
    clearRetryStates,
  } = useBulkEditHubContext();

  const hasEnterToConfirmBulk = useUserHasFeature(Feature.EnterToConfirmBulk);

  useEffect(() => {
    if (!hasEnterToConfirmBulk) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.key === 'Enter' &&
        stage === BulkEditStage.Preview &&
        submittButtonRef.current &&
        !submittButtonRef.current.disabled
      ) {
        submittButtonRef.current.click();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [hasEnterToConfirmBulk, stage, submittButtonRef]);

  const onCloseHandler = useCallback(
    async (newProgress?: BulkEditProgress) => {
      // We clean the progress if we want to cancel with retries
      if (retryableEntities) {
        clearRetryStates();
        setProgress(undefined);
      }
      if (stage === BulkEditStage.Preview) {
        setPreview(undefined);
        setStage(BulkEditStage.Idle);
      } else {
        if (
          stage === BulkEditStage.Idle &&
          retryableEntities &&
          progressBeforeRetry
        ) {
          onRetryCancel();
        }
        onClose(newProgress);
      }
    },
    [
      clearRetryStates,
      onClose,
      onRetryCancel,
      progressBeforeRetry,
      retryableEntities,
      setPreview,
      setProgress,
      setStage,
      stage,
    ]
  );

  const executeConfirmedSubmit = useCallback(() => {
    setStage(BulkEditStage.Execute);
    setErrors([]); // clear errors on start/retry
    setWarnings([]); // clear warnings on start/retry
    onSubmit();
  }, [onSubmit, setErrors, setStage, setWarnings]);

  const mergedListingDialog = useBasicDialog({
    onOkay: executeConfirmedSubmit,
  });

  const onSubmitHandler = useCallback(async () => {
    if (onBeforeSubmit && !(await onBeforeSubmit())) {
      return;
    }

    if (stage === BulkEditStage.Idle) {
      setStage(BulkEditStage.GettingPreview);
      onSubmit((preview) => {
        setPreview(preview);
        setStage(BulkEditStage.Preview);
      });
    } else if (preview?.hasMergedListings) {
      // Intercept confirm when affected purchases have merged listings
      mergedListingDialog.launchDialog();
    } else {
      executeConfirmedSubmit();
    }
  }, [
    executeConfirmedSubmit,
    mergedListingDialog,
    onBeforeSubmit,
    onSubmit,
    preview?.hasMergedListings,
    setPreview,
    setStage,
    stage,
  ]);

  return (
    <>
      <Button variant="outline" onClick={() => onCloseHandler(progress)}>
        {onCloseContentIdOverride ? (
          <Content id={onCloseContentIdOverride} />
        ) : stage === BulkEditStage.Idle &&
          retryableEntities &&
          progressBeforeRetry ? (
          <Content id={ContentId.Done} />
        ) : stage === BulkEditStage.GettingPreview ||
          stage === BulkEditStage.Preview ? (
          <Content id={ContentId.Back} />
        ) : hasBackgroundBulkEditFeature && progress ? (
          <Content id={ContentId.Close} />
        ) : (
          <Content id={ContentId.Cancel} />
        )}
      </Button>

      {(progress?.step !== BulkEditStep.Done ||
        Boolean(progress?.failureCount)) && (
        <Button
          ref={submittButtonRef}
          disabled={
            disabled ||
            stage === BulkEditStage.GettingPreview ||
            stage === BulkEditStage.Execute ||
            (stage === BulkEditStage.Preview &&
              !hasPrimaryAffectedIds(
                preview,
                primaryEntityTypesAffected ?? entityType
              )) ||
            bulkEditInProgress(
              hasBackgroundBulkEditFeature,
              isLoading,
              progress
            )
          }
          onClick={onSubmitHandler}
        >
          {onSubmitContentIdOverride ? (
            <Content id={onSubmitContentIdOverride} />
          ) : stage === BulkEditStage.Idle &&
            retryableEntities &&
            progressBeforeRetry ? (
            <Content id={ContentId.RetrySkipped} />
          ) : stage === BulkEditStage.Idle ||
            stage === BulkEditStage.GettingPreview ? (
            <Content id={ContentId.Preview} />
          ) : stage === BulkEditStage.Preview ||
            stage === BulkEditStage.Execute ? (
            <Content id={ContentId.Confirm} />
          ) : progress?.failureCount ? (
            <Content id={ContentId.Retry} />
          ) : (
            <Content id={ContentId.Save} />
          )}
        </Button>
      )}

      <ConfirmDialog
        {...mergedListingDialog.dialogProps}
        size="lg"
        headerText={<Content id={ContentId.UpdatePurchaseWithMergedListing} />}
        bodyText={
          <Stack direction="column" gap="l">
            <span>
              <Content id={ContentId.UpdatePurchaseWithMergedListingWarning} />
            </span>
            <span>
              <Content id={ContentId.AreYouSure} />
            </span>
          </Stack>
        }
        onOkay={mergedListingDialog.dialogProps.onOkay}
        onCancel={() => mergedListingDialog.closeDialog()}
      />
    </>
  );
};
